import { useEffect, useState } from "react";
import {
  getTrainingSessionGpuMetrics,
  type TrainingSessionGpuMetricsView,
} from "@/api/trainingEval";
import { resolveLoadErrorMessage } from "@/components/dataset";

// 서버에 캐시가 없어 요청마다 상류가 Kubernetes 와 Prometheus 를 조회한다. 계약의 하한이 15초다.
// 상태·학습 곡선 폴링과 같은 주기다.
export const gpuMetricsPollMs = 15_000;

export type SessionGpuMetricsState = {
  // 마지막 성공 응답. 아직 성공한 적이 없으면 null 이다.
  data: TrainingSessionGpuMetricsView | null;
  // 마지막 성공 응답을 받은 시각(ms). `data` 가 null 이면 null 이다.
  fetchedAtMs: number | null;
  // 가장 최근 요청의 실패 문구. 성공하면 null 이다. `data` 와 함께 있을 수 있다.
  error: string | null;
  refetch: () => void;
};

/**
 * 세션 학습 pod 의 CPU·메모리·GPU 순간 지표를 폴링한다. 진행 중인 세션의 카드가 마운트된 동안만 쓴다.
 *
 * - 15초 체인드 setTimeout 이다. 응답이 온 뒤에 다음 요청을 예약한다. 그래서 느린 응답이 겹치지 않는다.
 * - 폴링이 실패해도 마지막 성공 값을 유지한다. 순간 지표가 한 번의 상류 장애로 사라지지 않는다.
 * - 언마운트(탭 이탈·창 최소화·세션 종료)에서 요청과 타이머를 함께 끊는다.
 */
export function useSessionGpuMetrics(sessionId: string): SessionGpuMetricsState {
  const [state, setState] = useState<Omit<SessionGpuMetricsState, "refetch">>({
    data: null,
    fetchedAtMs: null,
    error: null,
  });
  const [refetchTick, setRefetchTick] = useState(0);

  useEffect(() => {
    const request = new AbortController();
    let timer: ReturnType<typeof setTimeout> | null = null;

    async function load(): Promise<void> {
      try {
        const data = await getTrainingSessionGpuMetrics(sessionId, { signal: request.signal });
        if (request.signal.aborted) {
          return;
        }
        setState({ data, fetchedAtMs: Date.now(), error: null });
      } catch (error) {
        if (request.signal.aborted) {
          return;
        }
        setState((prev) => ({ ...prev, error: resolveLoadErrorMessage(error) }));
      }
      timer = setTimeout(() => void load(), gpuMetricsPollMs);
    }

    // 다른 세션의 값이 새 세션의 카드에 남지 않게 한다.
    setState({ data: null, fetchedAtMs: null, error: null });
    void load();
    return () => {
      request.abort();
      if (timer !== null) {
        clearTimeout(timer);
      }
    };
  }, [sessionId, refetchTick]);

  return { ...state, refetch: () => setRefetchTick((tick) => tick + 1) };
}
